import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Bookmark, CheckCircle2, Layers3, Play, RotateCcw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getStations } from '../../lib/api'
import { getProgressStore, getProgressSummary, PROGRESS_STORAGE_EVENT } from '../../lib/progress'
import { useAuthSession } from '../../lib/firebase'
import type { Station } from '../../lib/types'
import './study.css'

type LoadState = { status: 'loading' } | { status: 'ready'; stations: Station[]; error?: string }

export function HomeLibraryPage() {
  const { user } = useAuthSession()
  const uid = user?.uid ?? ''
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    getStations({}, controller.signal)
      .then((result) => setState({ status: 'ready', stations: result.data }))
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ status: 'ready', stations: [], error: 'تعذر تحميل المحطات الآن. حدّث الصفحة وحاول مرة أخرى.' })
        }
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!uid) return undefined
    const refresh = (event: Event) => {
      const detail = (event as CustomEvent<{ uid?: string }>).detail
      if (!detail?.uid || detail.uid === uid) setRevision((value) => value + 1)
    }
    const storage = (event: StorageEvent) => {
      if (event.key === `ospe-progress:${uid}`) setRevision((value) => value + 1)
    }
    window.addEventListener(PROGRESS_STORAGE_EVENT, refresh)
    window.addEventListener('storage', storage)
    return () => {
      window.removeEventListener(PROGRESS_STORAGE_EVENT, refresh)
      window.removeEventListener('storage', storage)
    }
  }, [uid])

  const stations = state.status === 'ready' ? state.stations : []
  const summary = useMemo(() => getProgressSummary(uid, stations.map((station) => station.id)), [revision, stations, uid])
  const store = useMemo(() => getProgressStore(uid), [revision, uid])
  const ordered = useMemo(() => stations.slice().sort((a, b) => a.order - b.order || a.id.localeCompare(b.id)), [stations])
  const resume = ordered.find((station) => store.stations[station.id]?.mark === 'review')
    ?? ordered.find((station) => !store.stations[station.id] || store.stations[station.id].mark === 'unseen')
    ?? ordered[0]
  const questions = stations.reduce((total, station) => total + (station.questionCount ?? station.subquestions.length), 0)
  const completion = summary.total ? Math.round((summary.known / summary.total) * 100) : 0

  return (
    <section className="review-library" aria-labelledby="review-library-title">
      <header className="review-library__intro">
        <span className="review-library__kicker">مراجعة عملي الجراحة</span>
        <h1 id="review-library-title">كل محطات الجراحة<br /><em>في مكان واحد.</em></h1>
        <p>افتح أي محطة، أجب عن أسئلتها، ثم اكشف الإجابات وقيّم مستواك.</p>
      </header>

      {state.status === 'loading' ? (
        <div className="review-collection review-collection--loading" aria-busy="true">جارٍ تحميل المحطات…</div>
      ) : state.error || !resume ? (
        <div className="study-empty-state" role="alert">{state.status === 'ready' ? state.error : 'تعذر تحميل المحتوى.'}</div>
      ) : (
        <article className="review-collection">
          <div className="review-collection__visual" aria-hidden="true">
            <span>OSPE</span>
            <Layers3 size={42} strokeWidth={1.35} />
            <small>مراجعة عملي الجراحة</small>
          </div>
          <div className="review-collection__body">
            <div className="review-collection__topline">
              <span className="review-chip">محتوى المراجعة</span>
              <span>{stations.length} محطة · {questions} سؤال</span>
            </div>
            <h2>مراجعة عملي الجراحة</h2>
            <p>المحتوى منظّم في محطات، وقد تضم كل محطة سيناريو وصورًا وعدة أسئلة.</p>

            <div className="review-progress" aria-label={`اكتمل ${completion}%`}>
              <div><span>تقدمك</span><strong>{completion}%</strong></div>
              <span className="review-progress__track"><i style={{ width: `${completion}%` }} /></span>
            </div>

            <div className="review-collection__stats">
              <span><CheckCircle2 size={17} />{summary.known} أتقنتها</span>
              <span><RotateCcw size={17} />{summary.review} تحتاج مراجعة</span>
              <span><Bookmark size={17} />{summary.bookmarked} محفوظة</span>
            </div>

            <Link className="review-start" to={`/cases/${encodeURIComponent(resume.id)}`}>
              <Play size={18} fill="currentColor" />
              <span>{summary.known || summary.review ? 'متابعة المراجعة' : 'بدء المراجعة'}</span>
              <ArrowLeft size={18} />
            </Link>
          </div>
        </article>
      )}
    </section>
  )
}

export const LibraryPage = HomeLibraryPage
export default HomeLibraryPage
